/**
 * Carrying a hand-over request across the GitHub / GitLab round trip.
 *
 * The user leaves for the provider and comes back to `/github-auth`, a
 * different route, so nothing in `/authorize-app`'s or `/login`'s URL survives.
 * The platform already carries values across this hop — `nonHaRecipe`,
 * `haRecipe` and `email` go out to `GET /{github,gitlab}/auth-url` and come
 * back inside the base64 OAuth `state`. This request rides the same way, as one
 * base64 object (`serializeAuthorizeAppRequest`).
 *
 * `rememberAuthorizeAppRequest` / `readCarriedAuthorizeAppRequest` are the
 * same payload held in a cookie, which is how `zcp-pool-claim-base` keeps its
 * own flag across this hop and what the native (non-OAuth) login path uses.
 * Both vehicles carry the identical string, so the two paths cannot disagree
 * about what a request is.
 *
 * Everything read back goes through `parseAuthorizeAppRequest`, so a value that
 * is corrupt, forged, or no longer servable reads as absent rather than
 * resuming a hand-over on terms nobody asked for.
 */
import { asQuery, AuthorizeAppRequest, parseAuthorizeAppRequest } from './z3-handover.util';

export const AUTHORIZE_APP_CARRY_COOKIE = 'z3Handover';

/** Ten minutes: a provider round trip, and little enough that an abandoned tab expires. */
const CARRY_MAX_AGE = 600;

/** The wire form, shared with the OAuth `state` when that carrier lands. */
export const serializeAuthorizeAppRequest = (request: AuthorizeAppRequest): string => btoa(
  JSON.stringify(asQuery(request))
);

export const rememberAuthorizeAppRequest = (request: AuthorizeAppRequest): void => {
  if (typeof document === 'undefined') { return; }
  document.cookie =
    `${AUTHORIZE_APP_CARRY_COOKIE}=${serializeAuthorizeAppRequest(request)}; path=/; max-age=600; SameSite=Lax`;
};

export const forgetAuthorizeAppRequest = (): void => {
  if (typeof document === 'undefined') { return; }
  document.cookie = `${AUTHORIZE_APP_CARRY_COOKIE}=; path=/; max-age=0; SameSite=Lax`;
};

/**
 * Reads a carried request back, or null. Everything goes through
 * `parseAuthorizeAppRequest`, so a value that is corrupt, forged, or simply no
 * longer servable reads as absent rather than resuming on made-up terms.
 */
export const readCarriedAuthorizeAppRequest = (): AuthorizeAppRequest | null => {
  if (typeof document === 'undefined') { return null; }

  const row = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${AUTHORIZE_APP_CARRY_COOKIE}=`));
  if (!row) { return null; }

  const encoded = row.slice(AUTHORIZE_APP_CARRY_COOKIE.length + 1);
  if (!encoded) { return null; }

  try {
    const decoded = JSON.parse(atob(encoded)) as Record<string, string | undefined>;
    return parseAuthorizeAppRequest(decoded);
  } catch (e) {
    return null;
  }
};

// `CARRY_MAX_AGE` is inlined above so the literal is greppable from the cookie
// string itself; kept named for the reader.
void CARRY_MAX_AGE;

/**
 * A hand-over handed back inside the OAuth `state`, the way `nonHaRecipe` is.
 * Same encoding and same parser as the cookie, so the two paths out of the
 * sign-in cannot disagree about what a request is.
 */
export const readHandoverFromAuthState = (
  encoded: string | undefined
): AuthorizeAppRequest | null => {
  if (!encoded) { return null; }
  try {
    return parseAuthorizeAppRequest(JSON.parse(atob(encoded)) as Record<string, string | undefined>);
  } catch (e) {
    return null;
  }
};
